import { alpha, Theme } from '@mui/material/styles';
import { SURFACE_BORDER_RADIUS, getPrimaryTintSurface } from '../../theme/surfaceStyles';

export { SURFACE_BORDER_RADIUS, getPrimaryTintSurface };

const getSurfaceBorder = (theme: Theme, strength: 'soft' | 'medium' = 'medium') =>
  `1px solid ${alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'light' ? (strength === 'soft' ? 0.1 : 0.14) : strength === 'soft' ? 0.18 : 0.24
  )}`;

/** Пустая лента — карточка в стиле баннеров и блока «Дней вместе» */
export const getFeedContentEmptySx = (theme: Theme, interactive: boolean) => {
  const hoverAlpha = theme.palette.mode === 'light' ? 0.17 : 0.28;

  return {
    height: 400,
    mb: 3,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    textAlign: 'center' as const,
    borderRadius: `${SURFACE_BORDER_RADIUS}px`,
    border: getSurfaceBorder(theme),
    boxShadow:
      theme.palette.mode === 'light'
        ? `0 10px 32px ${alpha(theme.palette.common.black, 0.06)}`
        : `0 12px 40px ${alpha(theme.palette.common.black, 0.34)}`,
    overflow: 'hidden',
    transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), background-color 220ms ease',
    ...getPrimaryTintSurface(theme, {
      tint: { light: 0.1, dark: 0.18 },
    }),
    ...(interactive && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        bgcolor: alpha(theme.palette.primary.main, hoverAlpha),
      },
    }),
  };
};
export const getFeedContentEmptyIconSx = (theme: Theme) => ({
  width: 72,
  height: 72,
  mb: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.24),
  color: theme.palette.primary.main,
});

export const getFeedBannerPaperSx = (theme: Theme) => ({
  p: 2,
  borderRadius: `${SURFACE_BORDER_RADIUS}px`,
  position: 'relative' as const,
  ...getPrimaryTintSurface(theme, { interactive: true }),
});

/** Плавное закрытие баннеров на главной — схлопывание + лёгкий fade/slide */
export const feedBannerCollapseSx = {
  '& .feed-banner-shell': {
    transition: 'opacity 320ms ease, transform 320ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&.MuiCollapse-exiting .feed-banner-shell': {
    opacity: 0,
    transform: 'translateY(-8px)',
  },
} as const;

export const getPetSectionPaperSx = (theme: Theme) => ({
  px: 2.75,
  py: 2,
  mb: 3,
  borderRadius: `${SURFACE_BORDER_RADIUS}px`,
  width: '100%',
  ...getPrimaryTintSurface(theme),
});

export const getPetHintSurfaceSx = (theme: Theme) => ({
  px: 1.5,
  py: 1.25,
  borderRadius: `${Math.round(SURFACE_BORDER_RADIUS * 0.75)}px`,
  textAlign: 'center' as const,
  ...getPrimaryTintSurface(theme, {
    tint: { light: 0.14, dark: 0.24 },
  }),
});

export type PageTopGlowIntensity = 'default' | 'soft' | 'muted';

export const getPageTopGlowBackground = (
  theme: Theme,
  fadeTo: string,
  intensity: PageTopGlowIntensity = 'default'
) => {
  const { primary } = theme.palette;
  const isLight = theme.palette.mode === 'light';

  if (isLight) {
    switch (intensity) {
      case 'muted':
        return `radial-gradient(120% 72% at 50% -6%, ${alpha(primary.light, 0.17)} 0%, ${alpha(primary.main, 0.045)} 44%, ${fadeTo} 74%)`;
      case 'soft':
        return `radial-gradient(120% 72% at 50% -6%, ${alpha(primary.light, 0.26)} 0%, ${alpha(primary.main, 0.07)} 44%, ${fadeTo} 74%)`;
      case 'default':
        return `radial-gradient(120% 72% at 50% -6%, ${alpha(primary.light, 0.58)} 0%, ${alpha(primary.main, 0.16)} 44%, ${fadeTo} 74%)`;
      default: {
        const _exhaustive: never = intensity;
        return _exhaustive;
      }
    }
  }

  switch (intensity) {
    case 'muted':
      return `radial-gradient(120% 72% at 50% -6%, ${alpha(primary.main, 0.24)} 0%, ${alpha(primary.dark, 0.14)} 42%, ${fadeTo} 74%)`;
    case 'soft':
    case 'default':
      return `radial-gradient(120% 72% at 50% -6%, ${alpha(primary.main, 0.38)} 0%, ${alpha(primary.dark, 0.24)} 42%, ${fadeTo} 74%)`;
    default: {
      const _exhaustive: never = intensity;
      return _exhaustive;
    }
  }
};

type FullBleedTopGlowOptions = {
  top?: { xs: number | string; sm: number | string };
  height?: { xs: number | string; sm: number | string };
  /** На desktop glow рисует Layout от верха страницы — локальный ::before скрываем. */
  hideOnDesktop?: boolean;
};

/** На sm+ растягивает top-glow на всю ширину viewport — без видимых боковых границ у md-колонки. */
const getFullBleedTopGlowBeforeSx = (
  theme: Theme,
  intensity: PageTopGlowIntensity,
  options: FullBleedTopGlowOptions = {}
) => {
  const {
    top = { xs: 0, sm: 0 },
    height = { xs: 300, sm: 320 },
    hideOnDesktop = false,
  } = options;

  return {
    content: '""',
    position: 'absolute' as const,
    top,
    left: { xs: 0, sm: '50%' },
    right: { xs: 0, sm: 'auto' },
    width: { xs: 'auto', sm: '100vw' },
    transform: { xs: 'none', sm: 'translateX(-50%)' },
    height,
    background: getPageTopGlowBackground(theme, 'transparent', intensity),
    pointerEvents: 'none' as const,
    ...(hideOnDesktop ? { display: { xs: 'block', sm: 'none' } } : {}),
  };
};

/** Desktop Layout: мягкий glow от верха страницы, растянутый ниже по контенту. */
export const getDesktopLayoutTopGlowSx = (theme: Theme) => {
  const { primary } = theme.palette;
  const isLight = theme.palette.mode === 'light';
  const glow = isLight
    ? `radial-gradient(160% 110% at 50% -2%, ${alpha(primary.light, 0.28)} 0%, ${alpha(primary.main, 0.08)} 38%, transparent 82%)`
    : `radial-gradient(160% 110% at 50% -2%, ${alpha(primary.main, 0.22)} 0%, ${alpha(primary.dark, 0.12)} 38%, transparent 82%)`;

  return {
    position: 'relative' as const,
    bgcolor: theme.palette.background.default,
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: 920,
      background: glow,
      pointerEvents: 'none' as const,
      zIndex: 0,
    },
  };
};

/** Фон страницы питомца — на mobile свой glow; на desktop прозрачный, виден Layout-градиент */
export const getPetPageBackdropSx = (theme: Theme) => ({
  flex: 1,
  minHeight: '100%',
  width: '100%',
  position: 'relative' as const,
  bgcolor: { xs: theme.palette.background.default, sm: 'transparent' },
  '&::before': getFullBleedTopGlowBeforeSx(theme, 'default', { hideOnDesktop: true }),
});

type ChatDialogBackdropOptions = {
  /** Локальный glow на desktop (auth, диалог чата). Иначе на sm+ рисует Layout. */
  containGlow?: boolean;
  /** Высота top-glow; по умолчанию { xs: 300, sm: 320 }. */
  glowHeight?: { xs: number; sm: number };
  /** Интенсивность top-glow; по умолчанию soft. */
  glowIntensity?: PageTopGlowIntensity;
};

/** Фон диалога чата и вкладок (чат, календарь, новости, настройки) */
export const getChatDialogBackdropSx = (theme: Theme, options?: ChatDialogBackdropOptions) => ({
  position: 'relative' as const,
  // На desktop градиент рисует Layout — сплошной фон контейнера его перекрывает.
  // containGlow: полноэкранные оверлеи (открытый чат, auth) оставляют свой фон.
  bgcolor: options?.containGlow
    ? theme.palette.background.default
    : { xs: theme.palette.background.default, sm: 'transparent' },
  '&::before': getFullBleedTopGlowBeforeSx(theme, options?.glowIntensity ?? 'soft', {
    hideOnDesktop: !options?.containGlow,
    height: options?.glowHeight,
  }),
});

type FeedHeaderGlowOptions = {
  /** Растянуть glow до краёв родителя (нужен внутри MUI Container на главной). */
  bleed?: boolean;
  /** Мягче свечение для вкладок; на главной — default. */
  intensity?: PageTopGlowIntensity;
};

/** Свечение primary за шапкой («Привет, …» и вкладки). */
export const getFeedHeaderGlowSx = (theme: Theme, options: FeedHeaderGlowOptions = {}) => {
  const { bleed = false, intensity = 'default' } = options;
  const gutterXs = theme.spacing(2);
  const gutterSm = theme.spacing(3);

  return {
    position: 'relative' as const,
    mx: bleed
      ? { xs: `calc(-1 * ${gutterXs})`, sm: `calc(-1 * ${gutterSm})` }
      : 0,
    px: { xs: gutterXs, sm: gutterSm },
    // iOS PWA black-translucent: контент под status bar — отступ + glow заходят в safe-area.
    pt: {
      xs: `calc(${theme.spacing(0.5)} + env(safe-area-inset-top, 0px))`,
      sm: 0,
    },
    mt: { xs: -1, sm: -0.5 },
    pb: 0.5,
    '&::before': getFullBleedTopGlowBeforeSx(theme, intensity, {
      top: {
        xs: 'calc(-24px - env(safe-area-inset-top, 0px))',
        sm: -32,
      },
      height: {
        xs: 'calc(300px + env(safe-area-inset-top, 0px))',
        sm: 320,
      },
      hideOnDesktop: true,
    }),
  };
};

/** Фон блока с фото питомца */
export const getPetHeroBackground = (theme: Theme) => {
  const { primary } = theme.palette;

  if (theme.palette.mode === 'light') {
    return `linear-gradient(145deg, ${alpha(primary.light, 0.78)} 0%, ${alpha(primary.main, 0.42)} 46%, ${alpha(primary.dark, 0.24)} 100%)`;
  }

  return `linear-gradient(145deg, ${alpha(primary.dark, 0.62)} 0%, ${alpha(primary.main, 0.38)} 46%, ${alpha(primary.light, 0.2)} 100%)`;
};

/** Подсказка «Контент дня» — стекло с затемнением и blur как у блоков ленты */
export const getFeedContentUpdateTooltipSlotProps = (theme: Theme) => {
  const glassBg = alpha('#000', theme.palette.mode === 'light' ? 0.62 : 0.72);

  return {
    tooltip: {
      sx: {
        borderRadius: `${SURFACE_BORDER_RADIUS}px`,
        px: 2,
        py: 1.5,
        maxWidth: 300,
        fontSize: '0.8125rem',
        lineHeight: 1.45,
        color: '#fff',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        bgcolor: glassBg,
        boxShadow: `0 12px 40px ${alpha('#000', 0.28)}`,
        border: `1px solid ${alpha('#fff', 0.1)}`,
      },
    },
    arrow: {
      sx: {
        color: glassBg,
      },
    },
  };
};

export const NOTIFICATION_BELL_BASE_ROTATE = 16;

export const getNotificationBellIconSx = (fontSize: number, hasUnread: boolean) => {
  const baseRotate = `${NOTIFICATION_BELL_BASE_ROTATE}deg`;
  const origin = '50% 12%';

  if (!hasUnread) {
    return {
      fontSize,
      transform: `rotate(${baseRotate})`,
      transformOrigin: origin,
    };
  }

  return {
    fontSize,
    transformOrigin: origin,
    animation: 'feedNotificationBellRing 2.5s ease-in-out infinite',
    '@keyframes feedNotificationBellRing': {
      '0%, 58%, 100%': { transform: `rotate(${baseRotate})` },
      '6%': { transform: 'rotate(32deg)' },
      '12%': { transform: 'rotate(0deg)' },
      '18%': { transform: 'rotate(30deg)' },
      '24%': { transform: 'rotate(4deg)' },
      '30%': { transform: 'rotate(26deg)' },
      '36%': { transform: 'rotate(8deg)' },
      '42%': { transform: 'rotate(22deg)' },
      '48%': { transform: `rotate(${baseRotate})` },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transform: `rotate(${baseRotate})`,
    },
  };
};

/** Плавная пульсация кнопки колокольчика при непрочитанных уведомлениях */
export const getNotificationBellButtonAnimSx = (hasUnread: boolean) =>
  hasUnread
    ? {
        animation: 'feedNotificationBellButtonPulse 2.5s ease-in-out infinite',
        '@keyframes feedNotificationBellButtonPulse': {
          '0%, 58%, 100%': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)' },
          '30%': { boxShadow: '0 2px 14px rgba(255, 77, 109, 0.45)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }
    : {};

export const petCardEnterAnimation = (index: number) => ({
  animation: `petCardIn 0.38s cubic-bezier(0.22, 1, 0.36, 1) ${index * 0.06}s both`,
  '@keyframes petCardIn': {
    from: {
      opacity: 0,
      transform: 'scale(0.9) translateY(6px)',
    },
    to: {
      opacity: 1,
      transform: 'scale(1) translateY(0)',
    },
  },
});

export const petViewEnterSx = {
  '@keyframes petViewEnter': {
    from: {
      opacity: 0,
      transform: 'translateY(10px) scale(0.98)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  animation: 'petViewEnter 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
} as const;

export const petHorizontalScrollSx = {
  display: 'flex',
  gap: 1.5,
  overflowX: 'auto',
  pb: 0.5,
  mx: -0.25,
  px: 0.25,
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
} as const;

export const PET_SCROLL_CARD_WIDTH = 160;

export const petScrollItemSx = {
  flex: `0 0 ${PET_SCROLL_CARD_WIDTH}px`,
  width: PET_SCROLL_CARD_WIDTH,
  scrollSnapAlign: 'start',
  minHeight: 220,
} as const;
